import React, { useRef, useState } from "react";
import { Upload, X, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "../lib/api";

/**
 * Reusable upload widget. Calls POST /api/admin/upload, returns the file URL.
 * - props.value: current URL (string)
 * - props.onChange: (url|null) => void
 * - props.accept: MIME types
 * - props.label: input label
 * - props.kind: "image" | "document" — controls preview
 */
export default function FileUpload({ value, onChange, accept = "image/*,application/pdf", label = "Upload", kind = "image" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const fullUrl = value
        ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`)
        : null;

    const onPick = () => inputRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Uploaded");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Upload failed";
            toast.error(typeof msg === "string" ? msg : "Upload failed");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className="space-y-2">
            <input ref={inputRef} type="file" accept={accept} onChange={onFile} className="hidden" data-testid="file-upload-input" />
            {fullUrl ? (
                <div className="flex items-center gap-3 bg-[#0B1220] border border-[#243044] rounded-lg p-3">
                    {kind === "image" ? (
                        <img src={fullUrl} alt="" className="w-14 h-14 object-cover rounded-md border border-[#243044]" />
                    ) : (
                        <div className="w-14 h-14 rounded-md bg-[#111827] border border-[#243044] flex items-center justify-center">
                            <FileText className="w-6 h-6 text-[#56CCF2]" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#94A3B8] truncate">{value}</p>
                        <a href={fullUrl} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-wider text-[#56CCF2] hover:text-white">Open</a>
                    </div>
                    <button type="button" onClick={() => onChange(null)} className="p-1.5 text-[#94A3B8] hover:text-red-400 transition-colors" data-testid="file-upload-remove">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={onPick}
                    disabled={uploading}
                    data-testid="file-upload-button"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-[#243044] text-[#94A3B8] hover:text-[#56CCF2] hover:border-[#2F80ED] transition-colors text-sm"
                >
                    {uploading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                        </>
                    ) : (
                        <>
                            {kind === "image" ? <ImageIcon className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                            {label}
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
